/**
 * Agent profiles: one-click programs for a pane (Claude Code, Codex, Cursor
 * CLI, Grok, plus the user's own). Pure — the config store persists them,
 * the "+" menu lists them, `toLaunchSpec` turns one into the `launch` spec
 * `terminal_create` takes (resolved and spawned by Rust, terminal/launch.rs).
 *
 * Built-ins carry NO secrets and no env (owner rule: agents only ever see a
 * vault entry's NAME; Vault → env injection is not part of this feature).
 * `icon` is the lettermark fallback; built-ins render their brand icon
 * instead (agentIcons.js, by profile id).
 */

export const RESERVED_ENV = ['APIBOO_MCP_TOKEN'];
const ENV_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
export const CWD_MODES = ['inherit', 'home', 'fixed', 'ask'];

export const BUILTIN_PROFILES = [
  {
    id: 'claude',
    builtin: true,
    name: 'Claude Code',
    icon: { kind: 'lettermark', text: 'C', color: '#d97757' },
    command: 'claude',
    args: [],
    resumeArgs: ['--continue'],
    mcpSupport: 'claude',
    install: {
      win: 'irm https://claude.ai/install.ps1 | iex',
      mac: 'curl -fsSL https://claude.ai/install.sh | bash',
      linux: 'curl -fsSL https://claude.ai/install.sh | bash',
      alt: 'npm install -g @anthropic-ai/claude-code',
      docs: 'https://docs.anthropic.com/en/docs/claude-code/setup',
    },
  },
  {
    id: 'codex',
    builtin: true,
    name: 'Codex',
    icon: { kind: 'lettermark', text: 'Cx', color: '#10a37f' },
    command: 'codex',
    args: [],
    resumeArgs: ['resume', '--last'],
    mcpSupport: 'codex',
    install: {
      win: 'npm install -g @openai/codex',
      mac: 'npm install -g @openai/codex',
      linux: 'npm install -g @openai/codex',
      alt: 'brew install codex',
      docs: 'https://github.com/openai/codex',
    },
  },
  {
    id: 'cursor',
    builtin: true,
    name: 'Cursor CLI',
    icon: { kind: 'lettermark', text: 'Cu', color: '#3f3f46' },
    command: 'cursor-agent',
    args: [],
    resumeArgs: ['resume'],
    mcpSupport: null,
    install: {
      win: "irm 'https://cursor.com/install?win32=true' | iex",
      mac: 'curl https://cursor.com/install -fsS | bash',
      linux: 'curl https://cursor.com/install -fsS | bash',
      docs: 'https://cursor.com/cli',
    },
  },
  {
    id: 'grok',
    builtin: true,
    name: 'Grok',
    icon: { kind: 'lettermark', text: 'G', color: '#1f2937' },
    command: 'grok',
    args: [],
    resumeArgs: [],
    mcpSupport: null,
    install: {
      win: 'irm https://x.ai/cli/install.ps1 | iex',
      mac: 'curl -fsSL https://x.ai/cli/install.sh | bash',
      linux: 'curl -fsSL https://x.ai/cli/install.sh | bash',
      alt: 'npm i -g @xai-official/grok',
      docs: 'https://x.ai/cli',
    },
  },
];

const BUILTIN_IDS = new Set(BUILTIN_PROFILES.map((p) => p.id));
/** What a user may change on a built-in (the rest is fixed). */
const BUILTIN_EDITABLE = ['args', 'cwd', 'env', 'mcpAttach', 'hidden'];

const LETTERMARK_COLORS = ['#7c3aed', '#2563eb', '#0891b2', '#16a34a', '#ca8a04', '#dc2626', '#db2777'];

export function lettermarkFor(name) {
  const clean = String(name || '?').trim();
  const words = clean.split(/[\s_-]+/).filter(Boolean);
  const text = (words.length > 1 ? words[0][0] + words[1][0] : clean.slice(0, 2)).toUpperCase().replace(/^(.)(.)$/, (m, a, b) => a + b.toLowerCase());
  let h = 0;
  for (const ch of clean) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { kind: 'lettermark', text: text || '?', color: LETTERMARK_COLORS[h % LETTERMARK_COLORS.length] };
}

const strList = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);

/** Fill defaults and drop junk; never throws. */
export function normalizeProfile(p) {
  const src = p && typeof p === 'object' ? p : {};
  const cwd = src.cwd && CWD_MODES.includes(src.cwd.mode) ? { mode: src.cwd.mode, path: src.cwd.path || '' } : { mode: 'inherit' };
  const env = Array.isArray(src.env)
    ? src.env
        .filter((e) => e && typeof e.name === 'string')
        .map((e) => ({ name: e.name, value: typeof e.value === 'string' ? e.value : '', ...(e.enabled === false ? { enabled: false } : {}) }))
    : [];
  return {
    id: String(src.id || ''),
    builtin: !!src.builtin,
    name: String(src.name || src.command || 'Profile'),
    icon: src.icon && src.icon.kind === 'lettermark' ? src.icon : lettermarkFor(src.name || src.command),
    command: String(src.command || ''),
    args: strList(src.args),
    resumeArgs: strList(src.resumeArgs),
    cwd,
    env,
    mcpSupport: src.mcpSupport === 'claude' || src.mcpSupport === 'codex' ? src.mcpSupport : null,
    mcpAttach: src.mcpAttach !== false,
    install: src.install || null,
    hidden: !!src.hidden,
  };
}

/**
 * Validation errors keyed by field (empty object = valid). Keys are i18n
 * suffixes under `terminalExt.` so the editor can show them as they are.
 */
export function validateProfile(p) {
  const errors = {};
  if (!String(p.name || '').trim()) errors.name = 'errNameRequired';
  if (!String(p.command || '').trim()) errors.command = 'errCommandRequired';
  (p.env || []).forEach((e, i) => {
    if (e.enabled === false) return;
    if (!ENV_NAME_RE.test(e.name)) errors[`env.${i}`] = 'errEnvName';
    else if (RESERVED_ENV.includes(e.name.toUpperCase())) errors[`env.${i}`] = 'errEnvReserved';
  });
  if (p.cwd?.mode === 'fixed' && !String(p.cwd.path || '').trim()) errors.cwd = 'errCwdRequired';
  return errors;
}

/**
 * The effective profile list: built-ins (with the user's saved overrides of
 * the editable fields) first, then user profiles, in `order` if given.
 */
export function mergeProfiles(saved = []) {
  const byId = new Map((Array.isArray(saved) ? saved : []).filter((p) => p && p.id).map((p) => [p.id, p]));
  const builtins = BUILTIN_PROFILES.map((b) => {
    const o = byId.get(b.id) || {};
    const merged = { ...b };
    BUILTIN_EDITABLE.forEach((k) => {
      if (o[k] !== undefined) merged[k] = o[k];
    });
    return normalizeProfile(merged);
  });
  const users = [...byId.values()].filter((p) => !BUILTIN_IDS.has(p.id)).map((p) => normalizeProfile({ ...p, builtin: false }));
  return [...builtins, ...users];
}

/** What is saved for a profile: overrides only for built-ins, all for users. */
export function toSaved(p) {
  if (BUILTIN_IDS.has(p.id)) {
    const out = { id: p.id };
    BUILTIN_EDITABLE.forEach((k) => {
      if (p[k] !== undefined) out[k] = p[k];
    });
    return out;
  }
  const { builtin, install, ...rest } = p;
  return rest;
}

/**
 * The cwd a launch should use. `fixed` / `home` / `ask` keep their own folder
 * (they override the Terminal's default folder). `inherit` = the focused
 * pane's live cwd, else the tab's first pane cwd, else `ctx.defaultCwd` (the
 * Terminal's root folder), else home (undefined → Rust uses home).
 */
export function resolveCwd(profile, ctx = {}) {
  const mode = profile.cwd?.mode || 'inherit';
  if (mode === 'fixed') return profile.cwd.path || undefined;
  if (mode === 'home') return undefined;
  if (mode === 'ask') return ctx.askedCwd || undefined;
  return ctx.focusedCwd || ctx.tabCwd || ctx.defaultCwd || undefined;
}

/**
 * The pane spec for launching `profile`: what the store keeps on the pane
 * and the `launch` object sent to `terminal_create`. `resume` appends the
 * profile's resume args (layout restore). `mcp` ('claude'|'codex'|null)
 * asks Rust to attach the Apiboo MCP server (phase 4).
 */
export function toLaunchSpec(profile, ctx = {}) {
  const env = (profile.env || [])
    .filter((e) => e.enabled !== false && ENV_NAME_RE.test(e.name) && !RESERVED_ENV.includes(e.name.toUpperCase()))
    .map((e) => ({ name: e.name, value: e.value }));
  const args = [...(profile.args || []), ...(ctx.resume && profile.resumeArgs?.length ? profile.resumeArgs : [])];
  // profileId is only for Rust's log line (which profile launched); args
  // and env values are never logged.
  const launch = { program: profile.command, args, env, profileId: profile.id };
  const mcp = profile.mcpSupport && profile.mcpAttach !== false ? profile.mcpSupport : null;
  if (mcp && ctx.mcpAvailable !== false) launch.mcpAttach = mcp;
  return {
    kind: 'local',
    label: profile.name,
    profileId: profile.id,
    icon: profile.icon,
    launch,
    cwd: resolveCwd(profile, ctx),
    resumed: !!(ctx.resume && profile.resumeArgs?.length),
    // What a SPLIT of this pane launches: the same program without the
    // resume args (a second `--continue` would resume the same conversation).
    ...(ctx.resume && profile.resumeArgs?.length ? { freshLaunch: { ...launch, args: [...(profile.args || [])] } } : {}),
  };
}

/** `not_found:<program>` errors from terminal_create → the program name. */
export function notFoundProgram(error) {
  const m = /^not_found:(.+)$/.exec(String(error || ''));
  return m ? m[1] : null;
}

export function platformKey() {
  if (typeof navigator === 'undefined') return 'win';
  const p = `${navigator.platform || ''} ${navigator.userAgent || ''}`;
  if (/Mac|iPhone|iPad/i.test(p)) return 'mac';
  if (/Linux|X11/i.test(p)) return 'linux';
  return 'win';
}
